<template>
  <!-- With an action the whole tile is its button, and an empty tile offers it in place of "No photo" -->
  <component
    :is="actionLabel ? 'button' : 'div'"
    :type="actionLabel ? 'button' : undefined"
    :aria-label="actionLabel"
    :title="actionLabel"
    :class="['property-photo', size, { action: actionLabel }]"
  >
    <View
      v-if="showsNoPhoto || isUnableToLoad"
      direction="column"
      gap="xs"
      justify="center"
      align="center"
      class="property-image-placeholder"
    >
      <template v-if="showsNoPhoto && actionLabel">
        <Icon name="image-plus" :size="hasRoomForLabel ? 'medium' : 'small'" />
        <span v-if="hasRoomForLabel">{{ actionLabel }}</span>
      </template>
      <!-- The 32px and 48px tiles have no room for words: a home outline stands for the missing photo -->
      <Icon v-else-if="showsNoPhoto && !hasRoomForLabel" name="home-03" size="small" aria-label="No photo" />
      <span v-else-if="showsNoPhoto">No photo</span>
      <Icon v-else name="image-x" />
    </View>

    <template v-else>
      <!-- Holds the box while the photo downloads, so it never sits empty; the photo paints over it as it arrives -->
      <Skeleton v-if="!isLoaded" class="property-image-loading" width="100%" height="100%" />
      <img class="property-image" :src="imageUrl" alt="Property image" @load="onLoad" @error="onError" />
    </template>
  </component>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';

import { Icon, Skeleton, View } from '@tallo/design-system';

const props = defineProps<{
  imageUrl: string | undefined;
  size: 'xs' | 'sm' | 'md' | 'lg';
  /** What a click on the tile does, such as "Add cover"; without it the tile only shows the photo */
  actionLabel?: string;
  /** A photo that fails to load reads as no photo rather than a broken one, for a source that may simply have none (Street View) */
  noPhotoOnError?: boolean;
}>();

const isLoaded = ref(false);
const isUnableToLoad = ref(false);
// The 32px and 48px tiles have room for the icon alone; the label still names the button
const hasRoomForLabel = computed(() => props.size === 'md' || props.size === 'lg');
const showsNoPhoto = computed(() => !props.imageUrl || (props.noPhotoOnError && isUnableToLoad.value));

function onLoad() {
  isLoaded.value = true;
}

function onError() {
  isUnableToLoad.value = true;
}

// A new photo in the same box starts over: loading first, and a failure of the old one no longer applies
watch(
  () => props.imageUrl,
  () => {
    isLoaded.value = false;
    isUnableToLoad.value = false;
  },
);
</script>

<style scoped lang="scss">
$sizes: (
  'xs': '2rem',
  'sm': '3rem',
  'md': '5rem',
  'lg': '10rem',
);

.property-photo {
  position: relative;
  flex-shrink: 0;
  width: var(--property-image-size);
  height: var(--property-image-size);
  border-radius: var(--t-border-radius-sm);
  overflow: hidden;

  @each $size, $value in $sizes {
    &.#{$size} {
      --property-image-size: #{$value};
    }
  }
}

.property-image-placeholder,
.property-image-loading,
.property-image {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

.property-image-placeholder {
  background-color: var(--t-color-neutral-200);
  font-size: var(--t-font-size-300);
  line-height: 1.2;
  text-align: center;
}

.xs .property-image-placeholder,
.sm .property-image-placeholder {
  color: var(--t-text-color-tertiary);
}

.property-image {
  display: block;
  object-fit: cover;
}

// The listing photos' "Add photo" tile, at this tile's size
.action {
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;

  .property-image-placeholder {
    border: 1px dashed var(--t-color-neutral-600);
    border-radius: inherit;
    background-color: var(--t-color-neutral-white);
    color: var(--t-text-color-secondary);
    font: var(--t-typography-label-sm-font);
  }

  &:hover .property-image-placeholder {
    border-style: solid;
  }

  &:focus-visible {
    outline: 2px solid var(--t-color-neutral-950);
    outline-offset: 2px;
  }
}
</style>
